/**
 * Feature flags read from build-time `REACT_APP_*` env vars.
 *
 * These are baked into the JS bundle at `yarn build` time. The same
 * bundle ships to both web (tiplingo.com) and the iOS .ipa, so
 * flipping a flag requires a rebuild + (re)archive. That tradeoff is
 * acceptable for gating App-Review-sensitive surfaces — a runtime
 * flag would have its own attack surface.
 *
 * T49.P (Build 78) — STRICT OPT-IN semantics.
 * Build 77 shipped the visible "REFER A FRIEND" section because the
 * Mac's `frontend/.env` (gitignored) did NOT contain
 * `REACT_APP_REFERRAL_ENABLED=false`. The previous default-ON
 * behaviour interpreted "missing env var" as "feature ON for web
 * parity", which exposed the iOS bundle to App-Review-risky surface.
 *
 * Strict contract: `referralEnabled()` returns true ONLY when the env
 * var is EXACTLY the string `"true"` (case-insensitive, trimmed).
 * Missing, empty, undefined, `"false"`, or any other value → false.
 * The App-Review-safe state is therefore the *default* state.
 */

function _strictBool(envValue) {
  if (envValue === undefined || envValue === null) return false;
  return String(envValue).trim().toLowerCase() === "true";
}

/**
 * Refer-a-friend (Invite modal + Settings entry point).
 */
export function referralEnabled() {
  return _strictBool(process.env.REACT_APP_REFERRAL_ENABLED);
}

/**
 * T49.Q (Build 78) — Compile-time bundle marker.
 *
 * Anchored as `window.__tlBuildMarker = REFERRAL_BUILD_MARKER` in
 * `src/index.js` so webpack keeps the literal in the production
 * bundle (side-effect assignment can't be tree-shaken).
 *
 * The expression below is intentionally a straight-line ternary
 * over `process.env.REACT_APP_REFERRAL_ENABLED` (no function call,
 * no .trim() / .toLowerCase()) so CRA's DefinePlugin can constant-
 * fold the entire template at build time. Terser then collapses the
 * ternary against the inlined env value and emits a single string
 * literal like `"__TIPLINGO_REFERRAL=DISABLED__"`.
 *
 * Accepted source values (post-DefinePlugin inlining):
 *   `process.env.REACT_APP_REFERRAL_ENABLED` is undefined → UNSET
 *   value === "true"                                       → ENABLED
 *   anything else (including "false", "", "0", ...)        → DISABLED
 *
 * The release-gate greps for `__TIPLINGO_REFERRAL=DISABLED__`. App
 * Review-safe build = present. Stale / mis-flagged build = absent
 * (or ENABLED/UNSET marker present instead).
 */
export const REFERRAL_BUILD_MARKER =
  "__TIPLINGO_REFERRAL=" +
  (process.env.REACT_APP_REFERRAL_ENABLED === undefined
    ? "UNSET"
    : process.env.REACT_APP_REFERRAL_ENABLED === "true"
    ? "ENABLED"
    : "DISABLED") +
  "__";

// ─────────────────────────────────────────────────────────────────
// CompanyOS — Build 85+ post-approval feature flags (Founder D-008)
//
// Every new feature added post-Build-84 must be gated behind a flag
// declared here. Default: OFF. Turning a flag ON requires a rebuild
// AND a passing `verify-lockdown.sh` run before redeploy.
//
// Same DefinePlugin trick as REFERRAL_BUILD_MARKER: the ternary is
// a straight-line literal on `process.env.REACT_APP_*_ENABLED` so
// CRA constant-folds the whole expression at build time and Terser
// DCEs every `if (!flags.X)` branch when the flag is off.
// ─────────────────────────────────────────────────────────────────

// Currency Live Ticker + 💡 Money Tips popover. Backlog D-026.
export const TICKER_ENABLED = process.env.REACT_APP_TICKER_ENABLED === "true";
export const TICKER_BUILD_MARKER =
  "__TIPLINGO_TICKER=" +
  (process.env.REACT_APP_TICKER_ENABLED === undefined
    ? "UNSET"
    : process.env.REACT_APP_TICKER_ENABLED === "true"
    ? "ENABLED"
    : "DISABLED") +
  "__";

// Early Supporter promo tier ($19.99 first year) — kept off unless
// explicitly permitted by a future Apple review cycle.
export const EARLY_SUPPORTER_ENABLED =
  process.env.REACT_APP_EARLY_SUPPORTER_ENABLED === "true";

// Web-only AI credit packs — must remain OFF for the iOS bundle.
export const AI_CREDITS_ENABLED =
  process.env.REACT_APP_AI_CREDITS_ENABLED === "true";

// Android IAP via Google Play Billing — enabled only after Play
// Console listing is live and internal-test-track validated.
export const ANDROID_BILLING_ENABLED =
  process.env.REACT_APP_ANDROID_BILLING_ENABLED === "true";

// Preview-only fallback rates for the Currency Ticker. When TRUE and
// the batch endpoint is unreachable, the hook swaps in a labeled
// static rate map so the founder can still exercise the UI. NEVER
// enable this in production — the "Preview rates" label is a giveaway
// that the values are stale/mocked. See D-026 preview path.
export const TICKER_PREVIEW_MOCK_ENABLED =
  process.env.REACT_APP_TICKER_PREVIEW_MOCK_ENABLED === "true";

// Unified backend outage UI. When TRUE, the app installs a global
// health detector (axios interceptor + periodic /api/health ping)
// and mounts <BackendOutageBanner /> so users see one consistent
// friendly message instead of raw "HTTP (no response)" / "Voice
// translation failed" / axios ECONNABORTED toasts.
//
// Preview / TestFlight only until sign-off. Build 84 (gold master)
// must keep this OFF so the runtime behavior of the approved bundle
// is byte-for-byte unchanged. See INC-001 preventive action F and
// FOUNDER_DECISIONS.md D-034 (graceful degradation) + D-035 (outage
// messaging standard).
export const BACKEND_OUTAGE_UI_ENABLED =
  process.env.REACT_APP_BACKEND_OUTAGE_UI_ENABLED === "true";
export const BACKEND_OUTAGE_UI_BUILD_MARKER =
  "__TIPLINGO_BACKEND_OUTAGE_UI=" +
  (process.env.REACT_APP_BACKEND_OUTAGE_UI_ENABLED === undefined
    ? "UNSET"
    : process.env.REACT_APP_BACKEND_OUTAGE_UI_ENABLED === "true"
    ? "ENABLED"
    : "DISABLED") +
  "__";

